import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Switch,
  FormHelperText,
  Grid,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { teamSchema, type TeamFormData } from '@/lib/validations/team'

interface TeamFormProps {
  teamId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function TeamForm({ teamId, onSuccess, onCancel }: TeamFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!teamId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: '',
      description: '',
      lead_user_id: null,
      active: true,
    },
  })

  const activeValue = watch('active')

  useEffect(() => {
    if (isEditMode && teamId) {
      const load = async () => {
        setIsLoading(true)
        try {
          const res = await apiClient.get(`/organization/teams/${teamId}`)
          const d = res.data?.data ?? res.data ?? {}
          reset({
            name: d.name ?? '',
            description: d.description ?? '',
            lead_user_id: d.lead_user_id ?? null,
            active: d.active === true || d.active === 'Active',
          })
        } catch (e) {
          showError(e)
        } finally {
          setIsLoading(false)
        }
      }
      load()
    }
  }, [teamId, isEditMode, reset, showError])

  const onSubmit = async (data: TeamFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name,
        description: data.description?.trim() || null,
        lead_user_id: data.lead_user_id && !Number.isNaN(Number(data.lead_user_id)) ? Number(data.lead_user_id) : null,
        active: data.active,
      }
      if (isEditMode && teamId) {
        await apiClient.put(`/organization/teams/${teamId}`, payload)
        showSuccess('Success', 'Team updated successfully')
      } else {
        await apiClient.post('/organization/teams', payload)
        showSuccess('Success', 'Team created successfully')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading team...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. JFC Helpdesk 1" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.description}>
            <Textarea
              {...register('description')}
              placeholder="Optional short description of the team"
              rows={3}
            />
            <FormHelperText>Optional. Max 2000 characters.</FormHelperText>
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Switch
              isChecked={activeValue}
              onChange={(e) => setValue('active', e.target.checked)}
            />
            <FormHelperText>When inactive, this team is hidden from selection.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Team'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
